import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import {
  zslExecutionFailedErrorMsgAtom,
  sideBarSelectedAtom,
  isAiPageAtom,
  isViewOnlyModeAtom,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import { useComposer, type UIMessage } from "@zapier/agent-system";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import type { Message } from "@ai-sdk/ui-utils";

import { renderZslToCanvasSetter } from "../atoms/renderZslToCanvasSetter";
import { outlineAtom } from "../atoms/outlineAtom";
import { aiPromptAtom } from "../atoms/aiPromptAtom";
import { useSyncZslStreamToOutline } from "./useSyncZslStreamToOutline";
import {
  promptToZslErrorAtom,
  isPromptToZslCompleteAtom,
  isStreamingZslAtom,
} from "../atoms/streamingStatusAtoms";
import { tuningPromptsAtom } from "../atoms/tuningPromptsAtom";

export function usePromptToZslToCanvas() {
  const renderZslToCanvas = useSetAtom(renderZslToCanvasSetter);
  const setStringOutline = useSetAtom(outlineAtom);
  const [prompt, setPrompt] = useAtom(aiPromptAtom);
  const setTuningPrompts = useSetAtom(tuningPromptsAtom);
  const setError = useSetAtom(promptToZslErrorAtom);
  const toggles = useAtomValue(togglesAtomResettable);
  const isAiCobuildCopilot = toggles.aiCobuildCopilot;

  // Use appropriate chat system based on feature flag
  const { setMessages: setComposerMessages } = useComposer();
  const { setMessages: setCopilotMessages } = useCopilotChat();
  // const setCopilotMessages = () => {}
  // Choose the appropriate setMessages function
  const setMessages = isAiCobuildCopilot
    ? setCopilotMessages
    : setComposerMessages;

  const setIsViewOnlyMode = useSetAtom(isViewOnlyModeAtom);
  const setZslExecutionFailedErrorMsg = useSetAtom(
    zslExecutionFailedErrorMsgAtom,
  );
  const setIsStreamingZsl = useSetAtom(isStreamingZslAtom);
  const setSidebarSelected = useSetAtom(sideBarSelectedAtom);
  const setIsComplete = useSetAtom(isPromptToZslCompleteAtom);
  const isAiPage = useAtomValue(isAiPageAtom);
  useEffect(() => {
    if (isAiPage) {
      setSidebarSelected("composer");
    }
  }, [setSidebarSelected, isAiPage]);
  useEffect(() => {
    // Set messages in both composer and copilot systems
    if (isAiPage) {
      // Set messages using the appropriate setMessages function
      if (prompt) {
        if (isAiCobuildCopilot) {
          // For copilot, use Message format from @ai-sdk/ui-utils
          const messages: Message[] = [
            {
              role: "assistant",
              content: "", // Assistant will invoke the tool next
              id: "assistant-tool-call",
              createdAt: new Date(),
              parts: [
                { type: "text", text: prompt },
                {
                  type: "tool-invocation",
                  toolInvocation: {
                    state: "call",
                    toolName: "create_system",
                    toolCallId: "create_system",
                    args: {
                      prompt,
                    },
                  },
                },
              ],
            },
          ];
          setCopilotMessages(messages);
        } else {
          // For composer, use UIMessage format from @zapier/agent-system
          const messages: UIMessage[] = [
            {
              role: "assistant",
              content: prompt,
              id: "user-prompt",
              createdAt: new Date(),
              parts: [
                { type: "text", text: prompt },
                {
                  type: "tool-invocation",
                  toolInvocation: {
                    state: "call",
                    toolName: "create_system",
                    toolCallId: "create_system",
                    args: {
                      prompt,
                    },
                  },
                },
              ],
            },
          ];
          setComposerMessages(messages);
        }
      }
    }
  }, [isAiPage, isAiCobuildCopilot, prompt, setMessages]);

  const { handleSubmit } = useSyncZslStreamToOutline({
    api: "/api/zsl/v2/combined_stream",
    async onMessage() {},
    async onComplete(zsl) {
      setIsStreamingZsl(false);
      // If the user is on the AI Canvas Composer page, update the last tool invocation with the zsl result
      if (isAiPage) {
        setIsViewOnlyMode(false);

        if (isAiCobuildCopilot) {
          // For copilot, handle Message format
          setCopilotMessages((prevMessages: Message[]) => {
            return prevMessages.map((message: Message) => {
              const hasUpdateCall = message?.parts?.some(
                (invocation: any) =>
                  invocation.toolInvocation?.toolName === "create_system" &&
                  invocation.toolInvocation?.state === "call",
              );

              if (hasUpdateCall) {
                const completedToolInvocation = {
                  ...message,
                  parts: message.parts?.map((part: any) => {
                    if (
                      part.type === "tool-invocation" &&
                      part.toolInvocation?.toolName === "create_system" &&
                      part.toolInvocation?.state === "call"
                    ) {
                      return {
                        ...part,
                        toolInvocation: {
                          ...part.toolInvocation,
                          state: "result",
                          args: {
                            prompt,
                          },
                          result: {
                            content: [
                              {
                                text: `Canvas updated`,
                                zsl,
                              },
                            ],
                          },
                        },
                      };
                    }
                    return part;
                  }),
                };
                return completedToolInvocation;
              }
              return message;
            });
          });
        } else {
          // For composer, handle UIMessage format
          setComposerMessages((prevMessages: UIMessage[]) => {
            return prevMessages.map((message: UIMessage) => {
              const hasUpdateUiCall = message.parts?.some(
                (part: any) =>
                  part.type === "tool-invocation" &&
                  part.toolInvocation?.toolName === "create_system" &&
                  part.toolInvocation?.state === "call",
              );

              if (hasUpdateUiCall) {
                const completedToolInvocation = {
                  ...message,
                  parts: message.parts.map((part: any) => {
                    if (
                      part.type === "tool-invocation" &&
                      part.toolInvocation?.toolName === "create_system" &&
                      part.toolInvocation?.state === "call"
                    ) {
                      return {
                        ...part,
                        toolInvocation: {
                          ...part.toolInvocation,
                          state: "result",
                          args: {
                            prompt,
                          },
                          result: {
                            content: [
                              {
                                text: `Canvas updated`,
                                zsl,
                              },
                            ],
                          },
                        },
                      };
                    }
                    return part;
                  }),
                };
                return completedToolInvocation;
              }
              return message;
            });
          });
        }
      }

      await renderZslToCanvas();
      setIsComplete(true);
      setTimeout(() => {}, 1000);
    },
    onError(error: string) {
      setError(error);
    },
  });

  const onPromptSubmit = useCallback(
    async (localPrompt: string) => {
      setIsComplete(false);
      setStringOutline(""); // When user modify the prompt
      setZslExecutionFailedErrorMsg(null); // Clear out old error alert

      setPrompt(localPrompt);
      setTuningPrompts([]); // Reset tuning prompts for each new AI suggestion
      await handleSubmit(localPrompt);
    },
    [
      handleSubmit,
      setZslExecutionFailedErrorMsg,
      setIsComplete,
      setPrompt,
      setStringOutline,
      setTuningPrompts,
    ],
  );

  return {
    onPromptSubmit,
  };
}
